import { ReactNode, useState } from 'react';

import { ChevronRight } from 'lucide-react';

import { PopoverButton } from './PopoverButton';
import { SidebarSubItem } from './SidebarSubItem';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/atoms/Collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';
import { SidebarMenuItem, SidebarMenuButton, SidebarMenuSub } from '@/components/organisms/Sidebar/Sidebar';
import { useSidebar } from '@/components/organisms/Sidebar/Sidebar';

interface SubItem {
  title: string;
  onClick?: () => void;
  isLink?: boolean;
  href?: string;
}

interface CollapsibleSidebarItemProps {
  icon: ReactNode;
  title: string;
  openGroup: string | null;
  groupKey: string;
  onOpenChange: (open: boolean) => void;
  subItems?: SubItem[];
  className?: string;
}

export function CollapsibleSidebarItem({
  icon,
  title,
  openGroup,
  groupKey,
  onOpenChange,
  subItems = [],
  className = 'hover:bg-gray-100/20 cursor-pointer',
}: CollapsibleSidebarItemProps) {
  const { state: sidebarState } = useSidebar();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleItemClick = (onClick?: () => void) => {
    // Close the popover when any item is clicked
    setIsPopoverOpen(false);
    // Call the original onClick if it exists
    if (onClick) {
      onClick();
    }
  };

  return (
    <Collapsible asChild open={openGroup === groupKey} onOpenChange={onOpenChange} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          {sidebarState === 'collapsed' ? (
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <SidebarMenuButton tooltip={title} className={className}>
                  {icon}
                  <span>{title}</span>
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </PopoverTrigger>
              <PopoverContent
                side="right"
                align="start"
                className="m-2 w-36 border-gray-700 bg-gray-800 p-0 text-white shadow-2xl"
              >
                <div className="flex flex-col text-sm">
                  <div className="flex items-center gap-2 rounded-t-lg border-b border-b-gray-700 bg-gray-900 px-4 py-2 text-white">
                    {icon}
                    {title}
                  </div>
                  {subItems.map((item, index) => (
                    <PopoverButton key={index} onClick={() => handleItemClick(item.onClick)} asChild={item.isLink}>
                      {item.isLink ? (
                        <a href={item.href}>
                          <span>{item.title}</span>
                        </a>
                      ) : (
                        item.title
                      )}
                    </PopoverButton>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <SidebarMenuButton tooltip={title} className={className}>
              {icon}
              <span>{title}</span>
              <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className="border-gray-200">
            {subItems.map((item, index) => (
              <SidebarSubItem key={index} onClick={item.onClick}>
                {item.isLink ? (
                  <a href={item.href}>
                    <span>{item.title}</span>
                  </a>
                ) : (
                  item.title
                )}
              </SidebarSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
